import { memo, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { FeatureFlags } from '@constants/featureFlags';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useThrottle } from '@hooks/useThrottle';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

import { filter } from './filter';
import { getPathTo } from './getPathTo';
import { getScrollPercent } from './setScroll';
import { useSendFocusInteraction, useSendScrollEvent } from './useInteractions';

const CaptureFocus = () => {
  const snackbar = useSnackbar();
  const { isSyncedAppointment } = useAppointmentContext();
  const { pathname } = useLocation();
  const sendInteraction = useSendFocusInteraction();
  const sendScrollEvent = useSendScrollEvent();
  const throttledScrollEvent = useThrottle(sendScrollEvent, 100);
  const { value: isRsScrollSyncEnabled } = useFeatureFlag(
    FeatureFlags.RS_SCROLL_SYNC,
    false
  );

  const docRef = useRef(document);
  const doc = docRef.current;
  const shouldCaptureFocus = pathname !== '/presentation';

  const handleFocus = (e: Event) => {
    const target = e.target as HTMLElement;
    const path = getPathTo(target);
    sendInteraction(path);
    if (filter.includes(target.id) && isSyncedAppointment) {
      snackbar({
        message: 'Only you can see what is typed in this field',
        variant: SnackbarVariant.Info,
        preventDuplicate: true,
      });
    }
  };

  const handleScroll = useCallback(() => {
    const proportional = getScrollPercent();
    const raw = {
      y: window.scrollY,
      x: 0,
    };

    const payload = {
      proportional,
      raw,
    };

    throttledScrollEvent(JSON.stringify(payload));
  }, [throttledScrollEvent]);

  useEffect(() => {
    if (!shouldCaptureFocus) return;
    doc.addEventListener('focus', handleFocus, true);

    return () => {
      doc.removeEventListener('focus', handleFocus, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFocus, shouldCaptureFocus]);

  useEffect(() => {
    if (!shouldCaptureFocus) return;
    if (!isRsScrollSyncEnabled) return;
    doc.addEventListener('scroll', handleScroll, true);

    return () => {
      doc.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll, shouldCaptureFocus, isRsScrollSyncEnabled, doc]);

  return null;
};

const CaptureFocusMemo = memo(CaptureFocus);

export { CaptureFocusMemo as CaptureFocus };
